import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { createTasting, getUserWine, getWine, searchWines } from "../utils/apiClient";
import {
  buildWineTastingPayload,
  defaultWineTastingTitle,
  emptyWineIdentity,
  formatMultiAnswer,
  getFinalSteps,
  getTastingSteps,
  parseMultiAnswer,
  stepOptions,
  wineFromGrapeMinds,
} from "../utils/wineTastingGrid";
import WineTastingGridView from "../components/wine/WineTastingGridView";
import NoteChipsField from "../components/wine/NoteChipsField";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { compareTastingAnswers } from "../utils/wineTastingCompare";

function MultiChoiceField({ step, options, value, onChange }) {
  const selected = parseMultiAnswer(value);
  const [custom, setCustom] = useState("");

  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((v) => v !== opt)
      : [...selected, opt];
    onChange(formatMultiAnswer(next));
  };

  const addCustom = () => {
    const trimmed = custom.trim();
    if (!trimmed) return;
    onChange(formatMultiAnswer([...selected, trimmed]));
    setCustom("");
  };

  return (
    <Stack spacing={1.5}>
      <FormGroup>
        {options.map((opt) => (
          <FormControlLabel
            key={opt}
            control={<Checkbox checked={selected.includes(opt)} onChange={() => toggle(opt)} />}
            label={opt}
          />
        ))}
      </FormGroup>
      {step.allowCustom ? (
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            fullWidth
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={step.placeholder || "Tilføj…"}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustom();
              }
            }}
          />
          <Button variant="outlined" onClick={addCustom} disabled={!custom.trim()}>
            Tilføj
          </Button>
        </Stack>
      ) : null}
      {selected.length ? (
        <Typography variant="caption" color="text.secondary">
          Valgt: {selected.join(", ")}
        </Typography>
      ) : null}
    </Stack>
  );
}

function StepFields({ step, answers, onChange }) {
  const options = stepOptions(step, answers);
  const currentRaw = answers[step?.id];

  if (step?.multi && step?.chips) {
    return (
      <NoteChipsField
        value={parseMultiAnswer(currentRaw)}
        onChange={(next) => onChange(formatMultiAnswer(next))}
        suggestions={options}
        placeholder={step.placeholder || "Tilføj note…"}
      />
    );
  }
  if (step?.multi) {
    return (
      <MultiChoiceField
        step={step}
        options={options}
        value={currentRaw || ""}
        onChange={onChange}
      />
    );
  }
  if (step?.freeText) {
    return (
      <TextField
        value={currentRaw || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={step.placeholder || ""}
        fullWidth
        multiline={Boolean(step.multiline)}
        minRows={step.multiline ? 3 : 1}
        autoFocus
      />
    );
  }
  return (
    <FormControl>
      <RadioGroup value={currentRaw || ""} onChange={(e) => onChange(e.target.value)}>
        {options.map((opt) => (
          <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
        ))}
      </RadioGroup>
    </FormControl>
  );
}

function canProceed(step, answers) {
  if (!step) return false;
  if (step.skippable) return true;
  const raw = answers[step.id];
  if (step.multi) return parseMultiAnswer(raw).length > 0;
  if (step.freeText && step.multiline) return true;
  return Boolean(String(raw || "").trim());
}

function wineFromUserWine(item) {
  const v = item?.vintage || {};
  const year = v.is_non_vintage ? "" : v.vintage_year ? String(v.vintage_year) : "";
  return {
    ...emptyWineIdentity(),
    name: v.wine_name || "",
    producer: v.producer_name || "",
    grape: (v.grapes || []).join(", "),
    grapes: Array.isArray(v.grapes) ? v.grapes : [],
    region: v.geography_name || "",
    region_name: v.geography_name || "",
    country: v.country_name || "",
    vintage: year,
    grapeminds_id: v.grapeminds_id || "",
    color: v.color_name || "",
  };
}

function WineTastingSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blindParam = searchParams.get("blind");
  const blind = !(blindParam === "0" || blindParam === "false");
  const userWineUuid = searchParams.get("user_wine_uuid") || "";

  useDocumentTitle(blind ? "Blind smagning" : "Smagning");

  // tasting | review | final | search
  const [phase, setPhase] = useState("tasting");
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectingId, setSelectingId] = useState("");
  const [wine, setWine] = useState(emptyWineIdentity());
  const [wineVintageUuid, setWineVintageUuid] = useState(null);
  const [wineDetail, setWineDetail] = useState(null);
  const [bootLoading, setBootLoading] = useState(!blind && Boolean(userWineUuid));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const backTo = !blind && userWineUuid
    ? `/tools/wine-manager/bottle/${userWineUuid}`
    : "/wine-tasting";
  const pageTitle = blind ? "Blind smagning" : "Smagning";

  const tastingSteps = useMemo(() => getTastingSteps(answers, { blind }), [answers, blind]);
  const finalSteps = useMemo(() => getFinalSteps(answers, { blind }), [answers, blind]);
  const phaseSteps = phase === "final" ? finalSteps : tastingSteps;
  const safeIndex = Math.min(stepIndex, Math.max(phaseSteps.length - 1, 0));
  const step = phaseSteps[safeIndex];
  const isLastStep = phaseSteps.length > 0 && safeIndex === phaseSteps.length - 1;
  const wineReady = Boolean(wine.name.trim() || wine.grapeminds_id || wineVintageUuid);

  const comparisonBundle = useMemo(() => {
    if (!blind || !wineDetail) return null;
    return compareTastingAnswers(answers, wineDetail);
  }, [answers, wineDetail, blind]);

  const comparisons = useMemo(() => {
    if (!comparisonBundle) return null;
    const { __expected, ...rest } = comparisonBundle;
    return rest;
  }, [comparisonBundle]);

  const expected = comparisonBundle?.__expected || null;

  const loadLinkedWine = useCallback(async () => {
    if (blind || !userWineUuid) {
      setBootLoading(false);
      return;
    }
    setBootLoading(true);
    setError("");
    try {
      const item = await getUserWine(userWineUuid);
      setWine(wineFromUserWine(item));
      setWineVintageUuid(item.wine_vintage_uuid || null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Kunne ikke hente vinen til smagning");
    } finally {
      setBootLoading(false);
    }
  }, [blind, userWineUuid]);

  useEffect(() => {
    loadLinkedWine();
  }, [loadLinkedWine]);

  useEffect(() => {
    if (phase !== "tasting" && phase !== "final") return;
    if (stepIndex > phaseSteps.length - 1) {
      setStepIndex(Math.max(phaseSteps.length - 1, 0));
    }
  }, [phase, phaseSteps.length, stepIndex]);

  const setAnswer = (value) => {
    if (!step) return;
    setAnswers((prev) => ({ ...prev, [step.id]: value }));
  };

  const handleSearch = async (event) => {
    event?.preventDefault?.();
    const q = query.trim();
    if (q.length < 3) {
      setError("Søg med mindst 3 tegn.");
      return;
    }
    setSearching(true);
    setError("");
    setResults([]);
    setWine(emptyWineIdentity());
    setWineDetail(null);
    try {
      const data = await searchWines(q, { limit: 25 });
      setResults(Array.isArray(data?.results) ? data.results : []);
      if (!data?.results?.length) setError("Ingen vine fundet. Prøv et andet søgeord.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Søgning fejlede");
    } finally {
      setSearching(false);
    }
  };

  const handleSelectWine = async (hit) => {
    if (!hit?.id) return;
    setSelectingId(String(hit.id));
    setError("");
    try {
      const detail = await getWine(hit.id);
      setWineDetail(detail);
      setWine(wineFromGrapeMinds(detail));
    } catch (err) {
      setWineDetail(null);
      setWine({
        name: hit.name || "",
        producer: hit.producer || "",
        grape: "",
        region: "",
        vintage: hit.vintage || "",
        grapeminds_id: String(hit.id),
      });
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") setError(detail);
    } finally {
      setSelectingId("");
    }
  };

  const saveTasting = async () => {
    if (!wineReady) {
      setError(blind ? "Vælg en vin fra søgeresultaterne først." : "Vin mangler — gå tilbage til flasken.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = buildWineTastingPayload({
        wine,
        answers,
        comparisons: blind ? comparisons : null,
        expected: blind ? expected : null,
        blind,
      });
      const created = await createTasting({
        title: defaultWineTastingTitle(wine, { blind }),
        payload,
        wine_vintage_uuid: wineVintageUuid || null,
        grapeminds_id: wine.grapeminds_id || null,
        vintage_year: wine.vintage ? Number(wine.vintage) || null : null,
        is_non_vintage: !wine.vintage,
      });
      navigate(`/wine-tasting/${created.tasting_uuid}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Kunne ikke gemme smagning");
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    setError("");
    if ((phase === "tasting" || phase === "final") && !canProceed(step, answers)) {
      setError("Vælg eller udfyld et svar for at fortsætte.");
      return;
    }
    if (phase === "tasting") {
      if (!isLastStep) {
        setStepIndex(safeIndex + 1);
        return;
      }
      setPhase("review");
      return;
    }
    if (phase === "review") {
      if (!blind) {
        saveTasting();
        return;
      }
      setPhase("final");
      setStepIndex(0);
      return;
    }
    if (phase === "final") {
      if (!isLastStep) {
        setStepIndex(safeIndex + 1);
        return;
      }
      setPhase("search");
    }
  };

  const goBack = () => {
    setError("");
    if (phase === "tasting") {
      if (safeIndex > 0) setStepIndex(safeIndex - 1);
      return;
    }
    if (phase === "review") {
      setPhase("tasting");
      setStepIndex(Math.max(tastingSteps.length - 1, 0));
      return;
    }
    if (phase === "final") {
      if (safeIndex > 0) {
        setStepIndex(safeIndex - 1);
        return;
      }
      setPhase("review");
      return;
    }
    if (phase === "search") {
      setPhase("final");
      setStepIndex(Math.max(finalSteps.length - 1, 0));
    }
  };

  const handleSkip = () => {
    if (!step?.skippable) return;
    setAnswers((prev) => ({ ...prev, [step.id]: "" }));
    goNext();
  };

  const phaseLabel =
    phase === "tasting"
      ? "Smagning"
      : phase === "review"
        ? "Overblik"
        : phase === "final"
          ? "Endelig konklusion"
          : "Afslør vin";

  if (bootLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, pb: 8, maxWidth: 980, mx: "auto" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button component={RouterLink} to={backTo} variant="text" sx={{ px: 0, alignSelf: "flex-start" }}>
          Tilbage
        </Button>
        <Typography variant="caption" color="text.secondary">
          {phaseLabel}
          {phase === "tasting" || phase === "final"
            ? ` · ${safeIndex + 1} / ${phaseSteps.length}`
            : ""}
        </Typography>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}>
        {pageTitle}
      </Typography>
      {!blind && wine.name ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {[wine.producer, wine.name, wine.vintage].filter(Boolean).join(" · ")}
        </Typography>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}

      {phase === "review" ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {blind
              ? "Gennemgå dine noter før den endelige konklusion — så alder/årgang ikke modsiger hinanden uden at du ser det."
              : "Gennemgå dine noter, og gem smagningen."}
          </Typography>
          <WineTastingGridView
            answers={answers}
            wine={!blind ? wine : null}
            mode="tasting"
            blind={blind}
            title="Dine svar indtil videre"
          />
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={goBack}>Tilbage</Button>
            <Button variant="contained" onClick={goNext} disabled={saving}>
              {saving ? (
                <CircularProgress size={18} />
              ) : blind ? (
                "Videre til endelig konklusion"
              ) : (
                "Gem smagning"
              )}
            </Button>
          </Stack>
        </Stack>
      ) : null}

      {phase === "tasting" || phase === "final" ? (
        <Stack spacing={2} sx={{ maxWidth: 560 }}>
          {phase === "final" ? (
            <Typography variant="body2" color="text.secondary">
              Brug overblikket som støtte. Vælg ét konkret svar for hver del.
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            {step?.section}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {step?.label}
          </Typography>
          {step?.hint ? (
            <Typography variant="body2" color="text.secondary">
              {step.hint}
            </Typography>
          ) : null}
          <StepFields step={step} answers={answers} onChange={setAnswer} />
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Button onClick={goBack} disabled={phase === "tasting" && safeIndex === 0}>
              Tilbage
            </Button>
            <Stack direction="row" spacing={1}>
              {step?.skippable ? (
                <Button onClick={handleSkip}>Spring over</Button>
              ) : null}
              <Button
                variant="contained"
                onClick={goNext}
                disabled={!canProceed(step, answers)}
              >
                {phase === "tasting" && isLastStep
                  ? "Se overblik"
                  : phase === "final" && isLastStep
                    ? "Afslør vin"
                    : "Næste"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      ) : null}

      {phase === "search" ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Smagningen er færdig. Søg og vælg den rigtige vin — den gemmes sammen med dit grid.
          </Typography>
          <WineTastingGridView
            answers={answers}
            mode="all"
            blind={blind}
            title="Dit grid"
          />
          <Stack component="form" onSubmit={handleSearch} direction="row" spacing={1}>
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søg vin…"
            />
            <Button type="submit" variant="contained" disabled={searching}>
              {searching ? <CircularProgress size={18} /> : "Søg"}
            </Button>
          </Stack>
          <List
            sx={{
              maxHeight: 280,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {results.map((hit) => {
              const selected = String(wine.grapeminds_id) === String(hit.id);
              return (
                <ListItemButton
                  key={hit.id}
                  selected={selected}
                  disabled={Boolean(selectingId)}
                  onClick={() => handleSelectWine(hit)}
                >
                  <ListItemText
                    primary={hit.name || "Vin"}
                    secondary={
                      selectingId === String(hit.id)
                        ? "Henter…"
                        : [hit.producer, hit.color].filter(Boolean).join(" · ")
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
          {wineReady ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Valgt vin
              </Typography>
              <Typography variant="body2">{wine.name}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {[wine.producer, wine.region, wine.grape, wine.vintage].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          ) : null}
          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={goBack}>Tilbage</Button>
            <Button variant="contained" onClick={saveTasting} disabled={!wineReady || saving}>
              {saving ? <CircularProgress size={18} /> : "Gem smagning"}
            </Button>
          </Stack>
        </Stack>
      ) : null}
    </Box>
  );
}

export default WineTastingSession;
